import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import FileViewerModal from '../../LazyFileViewerModal';
import { firstName, formatFileSize, formatStamp } from '../utils/chatFormat';

/**
 * Opens chat images and files in the app's file viewer, as a gallery of
 * everything shared in the conversation (prev/next, arrow keys, filmstrip).
 * "Show in chat" closes the viewer and scrolls to the message.
 */
const ChatMediaContext = createContext({ openMedia: () => {} });

export const useChatMedia = () => useContext(ChatMediaContext);

export const REVEAL_MESSAGE_EVENT = 'lodgeic:chat:reveal-message';

export function ChatMediaProvider({ children }) {
  const [state, setState] = useState(null); // { items, index, conversation }

  const openMedia = useCallback((items, index = 0, { conversation } = {}) => {
    if (!items?.length) return;
    setState({ items, index: Math.max(0, Math.min(index, items.length - 1)), conversation });
  }, []);

  const close = useCallback(() => setState(null), []);

  const select = useCallback((i) => {
    setState((s) => (s && i >= 0 && i < s.items.length ? { ...s, index: i } : s));
  }, []);

  const reveal = useCallback((item) => {
    setState(null);
    if (item?.messageId) {
      window.dispatchEvent(new CustomEvent(REVEAL_MESSAGE_EVENT, { detail: { messageId: item.messageId } }));
    }
  }, []);

  const gallery = useMemo(() => {
    if (!state) return null;
    return {
      items: state.items.map((item) => ({
        ...item,
        meta: [firstName({ name: item.sender }), formatStamp(item.createdAt)].filter(Boolean).join(' · '),
      })),
      index: state.index,
      onSelect: select,
      onReveal: reveal,
    };
  }, [state, select, reveal]);

  const current = state ? state.items[state.index] : null;
  const subtitle = current
    ? [
      current.sender ? `${current.sender}${state.conversation ? ` in ${state.conversation}` : ''}` : state.conversation,
      formatStamp(current.createdAt),
      formatFileSize(current.size),
    ].filter(Boolean).join(' · ')
    : null;

  const value = useMemo(() => ({ openMedia }), [openMedia]);

  return (
    <ChatMediaContext.Provider value={value}>
      {children}
      {current && (
        <FileViewerModal
          url={current.url}
          fileType={current.fileType}
          fileName={current.fileName}
          gallery={gallery}
          subtitle={subtitle}
          onClose={close}
        />
      )}
    </ChatMediaContext.Provider>
  );
}
